import { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@cp/src/store';
import { Typography } from '@navi/web-ui/lib/primitives';
import Drawer from '@navi/web-ui/lib/primitives/Drawer';
import NaviTable from '@cp/src/components/NaviTable/NaviTable';
import { PaginationState } from '@tanstack/react-table';
import { getCallingAgentAuditDetails, getCallingAgentAudits } from '../action';
import { setSelectedAuditAssignmentReferenceId } from '../callAuditSlice';
import { QcAgentAudit } from '../interface';
import AuditFeedbackForm from '../components/AuditFeedbackForm';
import { getTDMEColumns } from './columns';
import LoaderIcon from '@cp/src/assets/icons/LoaderIcon';
import { DEFAULT_PAGINATION_STATE } from '../../ExternalDashboardSensei/constants';

const TDMEView = () => {
  const dispatch = useDispatch();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const loading = useSelector((state: RootState) => state.callAudit?.isTableLoading);
  const isDrawerLoading = useSelector((state: RootState) => state.callAudit?.isSidebarLoading);

  const currentPage = useSelector((state: RootState) => state.callAudit?.currentPage);
  const pageSize = useSelector((state: RootState) => state.callAudit?.pageSize);

  const callingAgentAudits = useSelector((state: RootState) => state.callAudit?.callingAgentAudits);

  const displayTotalElements = useSelector((state: RootState) => state.callAudit?.totalElements);

  useEffect(() => {
    dispatch(getCallingAgentAudits(currentPage, pageSize));
  }, []);

  const columns = useMemo(() => getTDMEColumns(), []);

  const handlePaginationChange = (paginationState: PaginationState) => {
    const newPage = paginationState.pageIndex;
    const newSize = paginationState.pageSize;
    dispatch(getCallingAgentAudits(newPage, newSize));
  };

  const handleRowClick = (row: QcAgentAudit) => {
    dispatch(setSelectedAuditAssignmentReferenceId(row.auditAssignmentReferenceId));
    dispatch(getCallingAgentAuditDetails(row.auditAssignmentReferenceId));
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  return (
    <div className="flex flex-col justify-center w-full">
      <Typography variant="h2" className="mb-5" color="var(--navi-color-gray-c1)">
        Calls
      </Typography>
      <NaviTable
        data={callingAgentAudits}
        columns={columns}
        totalElements={displayTotalElements}
        showPaginationComponent={true}
        defaultPaginationState={DEFAULT_PAGINATION_STATE}
        onPaginationChange={handlePaginationChange}
        loading={loading}
        enableRowClick={() => true}
        onRowClick={row => handleRowClick(row as QcAgentAudit)}
        alternateRowColor="var(--greyscale-content-4)"
      />

      <Drawer
        headerText="Audit Feedback"
        onClose={handleDrawerClose}
        open={isDrawerOpen}
        usePadding={false}
        showHeaderDivider={true}
        className="w-[450px]"
      >
        {isDrawerLoading ? (
          <div className="absolute h-full w-full bg-[var(--navi-color-gray-bg-primary)] top-0 flex items-center justify-center">
            <LoaderIcon
              size={40}
              borderWidth={4}
              borderColor="var(--navi-color-gray-border)"
              fillColor="var(--navi-color-blue-base)"
            />
          </div>
        ) : (
          <AuditFeedbackForm
            onSubmit={handleDrawerClose}
            readOnly={true}
            isDrawerOpen={isDrawerOpen}
          />
        )}
      </Drawer>
    </div>
  );
};

export default TDMEView;
